/**
 * Dashboard Layout - Responsive app shell
 * Mobile: Bottom nav option, hamburger menu
 * Desktop: Collapsible sidebar
 */
import { useState, useEffect } from 'react'
import { Outlet } from 'react-router-dom'
import { Bell } from 'lucide-react'
import { Sidebar, MobileSidebar, MobileSidebarTrigger } from './Sidebar'
import { UserMenu } from './UserMenu'
import { Button } from '../ui/button'
import { ThemeToggle } from '../theme-toggle'
import { CartButton, CartDrawer } from '../cart/CartDrawer'
import { useAuth } from '@mzanzi/auth'
import { cn } from '../../lib/utils'

function useSidebarCollapsed() {
  const [collapsed, setCollapsed] = useState(false)

  useEffect(() => {
    try {
      const stored = localStorage.getItem('sidebar-collapsed')
      if (stored) setCollapsed(JSON.parse(stored))
    } catch {
      // Corrupted localStorage value - use default
    }
  }, [])

  const toggle = () => {
    const next = !collapsed
    setCollapsed(next)
    localStorage.setItem('sidebar-collapsed', JSON.stringify(next))
  }

  return { collapsed, toggle }
}

function TopHeader({ onOpenMobileSidebar }: { onOpenMobileSidebar: () => void }) {
  const { user, logout } = useAuth()

  return (
    <header className="sticky top-0 z-30 h-14 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="flex h-full items-center justify-between px-4">
        <div className="flex items-center gap-2">
          <MobileSidebarTrigger onClick={onOpenMobileSidebar} />
        </div>
        <div className="flex items-center gap-1 sm:gap-2">
          <CartButton />
          <Button variant="ghost" size="icon" className="relative h-10 w-10">
            <Bell className="h-5 w-5" />
          </Button>
          <ThemeToggle />
          <UserMenu name={user?.name} email={user?.email} onLogout={logout} />
        </div>
      </div>
    </header>
  )
}

export function DashboardLayout() {
  const { collapsed, toggle } = useSidebarCollapsed()
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false)

  return (
    <div className="min-h-screen bg-background">
      <Sidebar collapsed={collapsed} onToggle={toggle} />
      <MobileSidebar open={mobileSidebarOpen} onOpenChange={setMobileSidebarOpen} />

      <div className={cn(
        'flex flex-col min-h-screen transition-all duration-300',
        collapsed ? 'lg:pl-[70px]' : 'lg:pl-[260px]'
      )}>
        <TopHeader onOpenMobileSidebar={() => setMobileSidebarOpen(true)} />

        <main className="flex-1 p-4 sm:p-6 lg:p-8">
          <Outlet />
        </main>

        <CartDrawer />

        <footer className="border-t py-3 px-4 text-center">
          <p className="text-xs text-muted-foreground">
            © {new Date().getFullYear()} Mzanzi Cloud
          </p>
        </footer>
      </div>
    </div>
  )
}
